import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import {
  Event as EventIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Today as TodayIcon,
  Schedule as ScheduleIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { fristenApi } from '../services/api';
import type { Frist, CreateFristDto } from '../types/api';

interface FristenWidgetProps {
  adresseId: number;
  adresseName?: string;
  compact?: boolean;
}

const FristenWidget: React.FC<FristenWidgetProps> = ({ adresseId, adresseName, compact = false }) => {
  const [fristen, setFristen] = useState<Frist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [formData, setFormData] = useState<CreateFristDto>({
    adresseID: adresseId,
    titel: '',
    beschreibung: '',
    kategorie: 'Allgemein',
    faelligkeitsdatum: new Date().toISOString().split('T')[0],
    prioritaet: 'Normal',
    istWiederholend: false,
  });

  useEffect(() => {
    loadFristen();
    loadKategorien();
  }, [adresseId]);

  const loadFristen = async () => {
    try {
      setLoading(true);
      const response = await fristenApi.getAll({ adresseId });
      setFristen(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Fristen:', err);
      setError('Fehler beim Laden der Fristen');
    } finally {
      setLoading(false);
    }
  };

  const loadKategorien = async () => {
    try {
      const response = await fristenApi.getKategorien();
      setKategorien(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Kategorien:', err);
    }
  };

  const handleCreate = async () => {
    try {
      await fristenApi.create(formData);
      setDialogOpen(false);
      resetForm();
      loadFristen();
    } catch (err: any) {
      console.error('Fehler beim Erstellen der Frist:', err);
      setError('Fehler beim Erstellen der Frist');
    }
  };

  const handleMarkAsErledigt = async (fristId: number) => {
    try {
      await fristenApi.markAsErledigt(fristId);
      loadFristen();
    } catch (err) {
      console.error('Fehler beim Markieren als erledigt:', err);
    }
  };

  const handleDelete = async (fristId: number) => {
    if (!window.confirm('Frist wirklich löschen?')) return;
    try {
      await fristenApi.delete(fristId);
      loadFristen();
    } catch (err) {
      console.error('Fehler beim Löschen der Frist:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      adresseID: adresseId,
      titel: '',
      beschreibung: '',
      kategorie: 'Allgemein',
      faelligkeitsdatum: new Date().toISOString().split('T')[0],
      prioritaet: 'Normal',
      istWiederholend: false,
    });
  };

  const getStatusColor = (frist: Frist) => {
    if (frist.status === 'Erledigt') return 'success';
    if (frist.istUeberfaellig) return 'error';
    if (frist.istHeute) return 'warning';
    if (frist.istDieseWoche) return 'info';
    return 'default';
  };

  const getStatusIcon = (frist: Frist) => {
    if (frist.status === 'Erledigt') return <CheckCircleIcon color="success" />;
    if (frist.istUeberfaellig) return <WarningIcon color="error" />;
    if (frist.istHeute) return <TodayIcon color="warning" />;
    return <EventIcon color="action" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const offeneFristen = fristen.filter((f) => f.status !== 'Erledigt');
  const ueberfaelligeFristen = fristen.filter((f) => f.istUeberfaellig);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <ScheduleIcon color="primary" />
          <Typography variant="h6">Fristen</Typography>
          {ueberfaelligeFristen.length > 0 && (
            <Chip
              label={`${ueberfaelligeFristen.length} überfällig`}
              color="error"
              size="small"
            />
          )}
        </Box>
        <Tooltip title="Neue Frist">
          <IconButton color="primary" onClick={() => setDialogOpen(true)}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {offeneFristen.length === 0 ? (
        <Typography color="textSecondary" textAlign="center" py={2}>
          Keine offenen Fristen
        </Typography>
      ) : (
        <List dense={compact}>
          {offeneFristen.slice(0, compact ? 5 : undefined).map((frist) => (
            <ListItem
              key={frist.fristID}
              secondaryAction={
                <Box>
                  <Tooltip title="Als erledigt markieren">
                    <IconButton
                      size="small"
                      onClick={() => handleMarkAsErledigt(frist.fristID)}
                      color="success"
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Löschen">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(frist.fristID)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              sx={{
                bgcolor: frist.istUeberfaellig ? 'error.50' : frist.istHeute ? 'warning.50' : 'transparent',
                borderRadius: 1,
                mb: 0.5,
              }}
            >
              <ListItemIcon>{getStatusIcon(frist)}</ListItemIcon>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" fontWeight="medium">
                      {frist.titel}
                    </Typography>
                    <Chip label={frist.kategorie} size="small" variant="outlined" />
                    {frist.istWiederholend && (
                      <Tooltip title={`Wiederholt sich alle ${frist.wiederholungIntervall} ${frist.wiederholungEinheit}`}>
                        <RepeatIcon fontSize="small" color="info" />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Chip
                      label={formatDate(frist.faelligkeitsdatum)}
                      size="small"
                      color={getStatusColor(frist)}
                    />
                    {frist.tageVerbleibend > 0 && (
                      <Typography variant="caption" color="textSecondary">
                        noch {frist.tageVerbleibend} Tag{frist.tageVerbleibend !== 1 ? 'e' : ''}
                      </Typography>
                    )}
                    {frist.tageVerbleibend < 0 && (
                      <Typography variant="caption" color="error">
                        {Math.abs(frist.tageVerbleibend)} Tag{Math.abs(frist.tageVerbleibend) !== 1 ? 'e' : ''} überfällig
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}

      {compact && offeneFristen.length > 5 && (
        <Typography variant="caption" color="textSecondary" textAlign="center" display="block">
          +{offeneFristen.length - 5} weitere Fristen
        </Typography>
      )}

      {/* Dialog für neue Frist */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neue Frist für {adresseName || 'Kunde'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              value={formData.titel}
              onChange={(e) => setFormData({ ...formData, titel: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Beschreibung"
              value={formData.beschreibung}
              onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
              multiline
              rows={2}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Kategorie</InputLabel>
              <Select
                value={formData.kategorie}
                label="Kategorie"
                onChange={(e) => setFormData({ ...formData, kategorie: e.target.value })}
              >
                {kategorien.map((kat) => (
                  <MenuItem key={kat} value={kat}>{kat}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Fälligkeitsdatum"
              type="date"
              value={formData.faelligkeitsdatum}
              onChange={(e) => setFormData({ ...formData, faelligkeitsdatum: e.target.value })}
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
            />
            <TextField
              label="Erinnerungsdatum (optional)"
              type="date"
              value={formData.erinnerungsdatum || ''}
              onChange={(e) => setFormData({ ...formData, erinnerungsdatum: e.target.value || undefined })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Priorität</InputLabel>
              <Select
                value={formData.prioritaet}
                label="Priorität"
                onChange={(e) => setFormData({ ...formData, prioritaet: e.target.value })}
              >
                <MenuItem value="Niedrig">Niedrig</MenuItem>
                <MenuItem value="Normal">Normal</MenuItem>
                <MenuItem value="Hoch">Hoch</MenuItem>
                <MenuItem value="Kritisch">Kritisch</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.istWiederholend}
                  onChange={(e) => setFormData({ ...formData, istWiederholend: e.target.checked })}
                />
              }
              label="Jährlich wiederholen"
            />
            {formData.istWiederholend && (
              <Box display="flex" gap={2}>
                <TextField
                  label="Intervall"
                  type="number"
                  value={formData.wiederholungIntervall || 1}
                  onChange={(e) => setFormData({ ...formData, wiederholungIntervall: parseInt(e.target.value) })}
                  sx={{ width: 100 }}
                />
                <FormControl sx={{ minWidth: 120 }}>
                  <InputLabel>Einheit</InputLabel>
                  <Select
                    value={formData.wiederholungEinheit || 'Jahre'}
                    label="Einheit"
                    onChange={(e) => setFormData({ ...formData, wiederholungEinheit: e.target.value })}
                  >
                    <MenuItem value="Tage">Tage</MenuItem>
                    <MenuItem value="Wochen">Wochen</MenuItem>
                    <MenuItem value="Monate">Monate</MenuItem>
                    <MenuItem value="Jahre">Jahre</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.erstelleTicket || false}
                  onChange={(e) => setFormData({ ...formData, erstelleTicket: e.target.checked })}
                />
              }
              label="Automatisch Ticket erstellen"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!formData.titel || !formData.faelligkeitsdatum}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default FristenWidget;
